import { NodeConnection, NodeRelation, NodeBase, OstNode, OstState, NodeType, UserModel, OpportunityNode, SolutionNode, StoryNode } from '../types';

// ─── ID Generation ───────────────────────────────────────────────────────────

/** Generates a unique id string for in-memory node identity. */
export function randomId(): string {
    return `id_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

// ─── Admin Guard ──────────────────────────────────────────────────────────────

/**
 * Returns true when the given user holds admin privileges.
 * Admin status is determined by matching the user's LinkedIn ID to the
 * environment-configured admin ID, making the check a single source of truth.
 */
export function isAdmin(user: UserModel | null): boolean {
    const adminId = process.env.REACT_APP_ADMIN_LINKEDIN_ID;
    if (!adminId || !user) return false;
    return user.linkedinId === adminId;
}

// ─── Node Type Helpers ────────────────────────────────────────────────────────

/**
 * Returns the allowed child node types for a given parent node type.
 * Used in both NodePanel and AddNodeModal to constrain the "Add child" options.
 */
export function childTypes(type: NodeType, solutionChildType: NodeType = 'story'): NodeType[] {
    if (type === 'outcome') return ['opportunity'];
    if (type === 'opportunity') return ['solution'];
    if (type === 'solution') return [solutionChildType || 'story'];
    return [];
}

/**
 * The single child type a node of `type` accepts, or undefined for leaf types
 * (story/experiment, which cannot have children).
 *
 * A convenience over `childTypes` for the common "one child type or nothing"
 * question. Callers had been re-deriving this as an inline `childTypeMap`
 * object literal, which is the same mapping written a second time.
 */
export function childTypeOf(type: NodeType, solutionChildType: NodeType = 'story'): NodeType | undefined {
    return childTypes(type, solutionChildType)[0];
}

// ─── Tree Traversal ───────────────────────────────────────────────────────────

/**
 * The four parallel node collections that make up a tree.
 *
 * A node's type determines which one it lives in: outcome -> `outcome`,
 * opportunity -> `opportunities`, solution -> `solutions`, and both story and
 * experiment -> `userStories`. Every helper below encodes that mapping once so
 * call sites stop re-deriving it with their own if/else chains.
 */
export type TreeCollections = Pick<OstState, 'outcome' | 'opportunities' | 'solutions' | 'userStories'>;

/**
 * Flattens the four collections into one array, in layer order.
 *
 * The tree is stored as four parallel arrays rather than a single node map, so
 * "every node" has to be assembled on demand; doing it inline is how the same
 * spread ended up written out at six separate call sites.
 */
export function flattenTreeNodes(tree: TreeCollections): OstNode[] {
    return [
        ...(tree.outcome ? [tree.outcome] : []),
        ...tree.opportunities,
        ...tree.solutions,
        ...tree.userStories,
    ];
}

/**
 * A brand-new, empty OST tree.
 *
 * The same literal was written out at five call sites (project load, project
 * creation, tree reset, `.zey` export fallback), which is how a field added to
 * OstState comes to be missing from some of them.
 *
 * `nodePanelPosition` defaults to `getDefaultNodePanelPosition()`, but two
 * callers historically hardcoded `{ x: 20, y: 120 }` instead — a left-aligned
 * panel rather than the right-aligned default. `overrides` preserves that
 * rather than silently changing where their panel opens; whether the two should
 * be reconciled is a product question, not a refactoring one.
 */
export function createEmptyOst(overrides: Partial<OstState> = {}): OstState {
    return {
        outcome: null,
        opportunities: [],
        solutions: [],
        userStories: [],
        positions: {},
        nodePanelPosition: getDefaultNodePanelPosition(),
        solutionChildType: 'story',
        ...overrides,
    };
}

// ─── Tree Mutation ────────────────────────────────────────────────────────────
//
// Each helper returns a NEW TreeCollections with only the affected collection
// replaced. They are deliberately written as one switch each, rather than a
// generic "apply fn to the right array" abstraction, so every branch keeps its
// own concrete node type and TypeScript still checks the assignment.

/** Removes a node from whichever collection its type lives in. */
export function removeNodeFromTree(tree: TreeCollections, type: NodeType, nodeId: string): TreeCollections {
    switch (type) {
        case 'outcome':
            // Guarded on id so a stale call cannot clear an unrelated outcome.
            return tree.outcome?.id === nodeId ? { ...tree, outcome: null } : tree;
        case 'opportunity':
            return { ...tree, opportunities: tree.opportunities.filter((n) => n.id !== nodeId) };
        case 'solution':
            return { ...tree, solutions: tree.solutions.filter((n) => n.id !== nodeId) };
        default:
            return { ...tree, userStories: tree.userStories.filter((n) => n.id !== nodeId) };
    }
}

/** Shallow-merges `data` into the node with `nodeId`, in its own collection. */
export function updateNodeInTree(
    tree: TreeCollections,
    type: NodeType,
    nodeId: string,
    data: Partial<NodeBase>,
): TreeCollections {
    // Generic so each collection keeps its own concrete node type: spreading a
    // Partial<NodeBase> directly would widen `type` back to the full NodeType union.
    const merge = <T extends NodeBase>(node: T): T => ({ ...node, ...data });
    const mergeMatching = <T extends NodeBase>(list: T[]): T[] =>
        list.map((n) => (n.id === nodeId ? merge(n) : n));

    switch (type) {
        case 'outcome':
            return tree.outcome?.id === nodeId ? { ...tree, outcome: merge(tree.outcome) } : tree;
        case 'opportunity':
            return { ...tree, opportunities: mergeMatching(tree.opportunities) };
        case 'solution':
            return { ...tree, solutions: mergeMatching(tree.solutions) };
        default:
            return { ...tree, userStories: mergeMatching(tree.userStories) };
    }
}

/** Appends an already-constructed node to the collection for its type. */
export function appendNodeToTree(
    tree: TreeCollections,
    node: OpportunityNode | SolutionNode | StoryNode,
): TreeCollections {
    switch (node.type) {
        case 'opportunity':
            return { ...tree, opportunities: [...tree.opportunities, node] };
        case 'solution':
            return { ...tree, solutions: [...tree.solutions, node] };
        default:
            return { ...tree, userStories: [...tree.userStories, node] };
    }
}

// ─── Layout Computation ───────────────────────────────────────────────────────

const LAYER_Y_OFFSET: Record<NodeType, number> = {
    outcome: 0,
    opportunity: 140,
    solution: 280,
    story: 420,
    experiment: 420,
    enablerNode: 420,
};

const HORIZONTAL_GAP = 220;
const VERTICAL_GAP = 140;

export const NODE_PANEL_WIDTH = 360;
export const NODE_PANEL_MARGIN = 20;
export const NODE_PANEL_DEFAULT_Y = 120;

export function getDefaultNodePanelPosition(): { x: number; y: number } {
    const screenWidth = typeof window !== 'undefined' ? window.innerWidth : 1200;
    const x = Math.max(NODE_PANEL_MARGIN, screenWidth - NODE_PANEL_WIDTH - NODE_PANEL_MARGIN);
    return { x, y: NODE_PANEL_DEFAULT_Y };
}

/**
 * A persisted panel position can go stale relative to the current window (e.g. it was
 * saved from a much wider screen). Returns false once the panel would render mostly or
 * fully off-screen, so callers can fall back to getDefaultNodePanelPosition() instead of
 * trusting a stored position blindly.
 */
export function isNodePanelPositionOnScreen(pos: { x: number; y: number }): boolean {
    if (typeof window === 'undefined') return true;
    return (
        pos.x >= 0 &&
        pos.y >= 0 &&
        pos.x + NODE_PANEL_WIDTH <= window.innerWidth + NODE_PANEL_MARGIN &&
        pos.y <= window.innerHeight - NODE_PANEL_MARGIN
    );
}

/**
 * Calculates a non-colliding canvas position for a new node.
 *
 * @param type            - The node type being placed.
 * @param parentPos       - Canvas position of the parent node, if any.
 * @param existingPositions - Map of all current node positions (for collision avoidance).
 * @param override        - An explicit position that bypasses auto-layout entirely.
 * @returns               The computed { x, y } canvas position.
 */
export function computeNodePosition(
    type: NodeType,
    parentPos: { x: number; y: number } | null,
    existingPositions: Record<string, { x: number; y: number }>,
    override?: { x: number; y: number } | null,
): { x: number; y: number } {
    if (override) return override;

    const allPositions = Object.values(existingPositions);

    const getOccupiedXs = (y: number) =>
        allPositions.filter((pos) => Math.abs(pos.y - y) < 40).map((pos) => pos.x);

    const findFreeX = (preferredX: number, y: number): number => {
        const occupied = getOccupiedXs(y);
        const candidates = [0];
        for (let i = 1; i <= 8; i += 1) {
            candidates.push(i * HORIZONTAL_GAP, -i * HORIZONTAL_GAP);
        }
        for (const offset of candidates) {
            const x = preferredX + offset;
            const collides = occupied.some((xx) => Math.abs(xx - x) < HORIZONTAL_GAP * 0.9);
            if (!collides) return x;
        }
        return preferredX;
    };

    if (parentPos) {
        const targetY = parentPos.y + VERTICAL_GAP;
        return { x: findFreeX(parentPos.x, targetY), y: targetY };
    }

    const targetY = LAYER_Y_OFFSET[type] ?? 0;
    return { x: findFreeX(0, targetY), y: targetY };
}

function uniqueRelationKey(r: NodeRelation) {
    return `${r.nodeId}:${r.type}:${r.direction || 'outgoing'}`;
}

export function addRelationOnce(relations: NodeRelation[] | undefined, relation: NodeRelation): NodeRelation[] {
    const existing = relations || [];
    const key = uniqueRelationKey(relation);
    if (existing.some((r) => uniqueRelationKey(r) === key)) return existing;
    return [...existing, relation];
}

export function removeRelationOnce(relations: NodeRelation[] | undefined, relation: NodeRelation): NodeRelation[] {
    const targetKey = uniqueRelationKey(relation);
    return (relations || []).filter((r) => uniqueRelationKey(r) !== targetKey);
}

/**
 * How a connection endpoint's relation list is transformed — `addRelationOnce`
 * to wire a connection up, `removeRelationOnce` to tear it down.
 */
export type RelationTransform = (relations: NodeRelation[] | undefined, relation: NodeRelation) => NodeRelation[];

/**
 * Rewrites the relation lists of a connection's two endpoints across all four
 * collections, tagging the source `outgoing` and the target `incoming`.
 *
 * addConnection and removeConnection were mirror images of each other — the
 * same four-collection traversal and the same endpoint/direction logic, written
 * twice and differing only in this transform.
 *
 * Nodes that neither endpoint references are returned by reference, as is an
 * endpoint whose relations the transform left untouched (addRelationOnce
 * returns its input array when the relation already exists), so unaffected
 * nodes keep their identity for React.
 */
export function applyConnectionAcrossTree(
    tree: TreeCollections,
    connection: NodeConnection,
    transform: RelationTransform,
): TreeCollections {
    const apply = <T extends NodeBase>(node: T): T => {
        let relation: NodeRelation | null = null;
        if (node.id === connection.fromNodeId) {
            relation = { nodeId: connection.toNodeId, type: connection.type, direction: 'outgoing' };
        } else if (node.id === connection.toNodeId) {
            relation = { nodeId: connection.fromNodeId, type: connection.type, direction: 'incoming' };
        }
        if (!relation) return node;

        const nextRelations = transform(node.relations, relation);
        return nextRelations === node.relations ? node : { ...node, relations: nextRelations };
    };

    return {
        outcome: tree.outcome ? apply(tree.outcome) : null,
        opportunities: tree.opportunities.map(apply),
        solutions: tree.solutions.map(apply),
        userStories: tree.userStories.map(apply),
    };
}

export function normalizeOstState(raw: Partial<OstState>): OstState {
    const outcome = raw.outcome ? { ...raw.outcome, relations: [...(raw.outcome.relations || [])] } : null;
    const opportunities = (raw.opportunities || []).map((n) => ({ ...n, relations: [...(n.relations || [])] }));
    const solutions = (raw.solutions || []).map((n) => ({ ...n, relations: [...(n.relations || [])] }));
    const userStories = (raw.userStories || []).map((n) => ({ ...n, relations: [...(n.relations || [])] }));

    const nodes: Record<string, OstNode> = {};
    if (outcome) nodes[outcome.id] = outcome;
    opportunities.forEach((n) => { nodes[n.id] = n; });
    solutions.forEach((n) => { nodes[n.id] = n; });
    userStories.forEach((n) => { nodes[n.id] = n; });

    const connections: NodeConnection[] = Array.isArray(raw.connections) ? [...raw.connections] : [];

    // Normalize existing relations on each node to explicit direction values and dedupe.
    Object.values(nodes).forEach((node) => {
        const existingRelations = node.relations || [];
        let normalizedRelations: NodeRelation[] = [];

        existingRelations.forEach((rel) => {
            const relationType = rel.type || 'related';
            let direction = rel.direction;
            if (!direction) {
                // infer direction using the connection list when possible
                if (connections.some((c) => c.fromNodeId === node.id && c.toNodeId === rel.nodeId)) {
                    direction = 'outgoing';
                } else if (connections.some((c) => c.toNodeId === node.id && c.fromNodeId === rel.nodeId)) {
                    direction = 'incoming';
                } else {
                    direction = 'outgoing';
                }
            }
            normalizedRelations = addRelationOnce(normalizedRelations, {
                nodeId: rel.nodeId,
                type: relationType,
                direction,
            });
        });

        node.relations = normalizedRelations;
    });

    // Add relation entries for connections (bi-directional source+target metadata)
    connections.forEach((conn) => {
        const from = nodes[conn.fromNodeId];
        const to = nodes[conn.toNodeId];
        if (!from || !to) return;

        from.relations = addRelationOnce(from.relations, {
            nodeId: conn.toNodeId,
            type: conn.type || 'related',
            direction: 'outgoing',
        });

        to.relations = addRelationOnce(to.relations, {
            nodeId: conn.fromNodeId,
            type: conn.type || 'related',
            direction: 'incoming',
        });
    });

    // Build connection objects for explicit relation lists (legacy support), if missing
    const connectionSet = new Map<string, NodeConnection>();
    connections.forEach((conn) => connectionSet.set(`${conn.fromNodeId}:${conn.toNodeId}:${conn.type}`, conn));

    Object.values(nodes).forEach((node) => {
        (node.relations || []).forEach((rel) => {
            if (rel.direction === 'incoming') return; // skip reverse direction in node.relations
            if (!nodes[rel.nodeId]) return;
            const key = `${node.id}:${rel.nodeId}:${rel.type}`;
            if (!connectionSet.has(key)) {
                const newConn: NodeConnection = {
                    id: `conn_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`,
                    fromNodeId: node.id,
                    toNodeId: rel.nodeId,
                    type: rel.type || 'related',
                    fromOffset: { x: 0, y: 0 },
                    toOffset: { x: 0, y: 0 },
                };
                connectionSet.set(key, newConn);
            }
        });
    });

    return {
        outcome,
        opportunities,
        solutions,
        userStories,
        positions: raw.positions || {},
        nodePanelPosition: raw.nodePanelPosition || getDefaultNodePanelPosition(),
        solutionChildType: raw.solutionChildType || 'story',
        connections: Array.from(connectionSet.values()),
        // Pass-through insight fields so they survive every setOst call (DB load, import, etc.)
        insights: raw.insights || [],
        insightLinks: raw.insightLinks || [],
        insightsPanelPosition: raw.insightsPanelPosition || { x: 20, y: 260 },
        insightsPanelMinimized: raw.insightsPanelMinimized ?? false,
        // Pass-through triage fields
        triageItems: raw.triageItems || [],
        triagePanelMinimized: raw.triagePanelMinimized ?? false,
    };
}

export function applyConnectionToNodeBase(node: NodeBase | null, connection: NodeConnection): NodeBase | null {
    if (!node) return null;
    const rel: NodeRelation = { nodeId: connection.toNodeId, type: connection.type, direction: 'outgoing' };
    const relations = addRelationOnce(node.relations, rel);
    return { ...node, relations };
}

export function applyConnectionRemovalToNodeBase(node: NodeBase | null, connection: NodeConnection): NodeBase | null {
    if (!node) return null;
    const rel: NodeRelation = { nodeId: connection.toNodeId, type: connection.type, direction: 'outgoing' };
    const relations = removeRelationOnce(node.relations, rel);
    return { ...node, relations };
}

/**
 * Percent (0-100) of an outcome's user-set progress-bar reading (`progressPercent`), shared by
 * the node panel's slider/progress bar and the canvas's mini progress bar so both always agree.
 * Returns null when unset/non-numeric, meaning there's nothing to plot yet.
 */
export function getOutcomeProgressPercent(node: Pick<NodeBase, 'progressPercent'>): number | null {
    const percent = parseFloat(node.progressPercent ?? '');
    if (Number.isNaN(percent)) return null;
    return Math.max(0, Math.min(100, percent));
}

/** Orange -> amber -> green stops the outcome progress bar's solid fill color is interpolated across. */
const PROGRESS_COLOR_STOPS: [number, [number, number, number]][] = [
    [0, [249, 115, 22]], // orange
    [50, [245, 158, 11]], // amber
    [100, [16, 185, 129]], // green
];

/**
 * Solid fill color for the outcome progress bar at a given percent (0-100), interpolated across
 * PROGRESS_COLOR_STOPS. Shared by the node panel's progress bar and the canvas's mini progress
 * bar so both always agree.
 */
export function getOutcomeProgressColor(percent: number): string {
    const clamped = Math.max(0, Math.min(100, percent));
    const [lower, upper] = clamped <= 50
        ? [PROGRESS_COLOR_STOPS[0], PROGRESS_COLOR_STOPS[1]]
        : [PROGRESS_COLOR_STOPS[1], PROGRESS_COLOR_STOPS[2]];

    const span = upper[0] - lower[0];
    const t = span === 0 ? 0 : (clamped - lower[0]) / span;
    const [r1, g1, b1] = lower[1];
    const [r2, g2, b2] = upper[1];
    const r = Math.round(r1 + (r2 - r1) * t);
    const g = Math.round(g1 + (g2 - g1) * t);
    const b = Math.round(b1 + (b2 - b1) * t);
    return `rgb(${r}, ${g}, ${b})`;
}
